<template>
  <main class="py-4 sm:py-8 sm:p-8">
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>Gerencie seus Order Bumps</PageTitle>
        <PageDescription>
          Ofereça produtos complementares no momento do checkout para aumentar seu ticket médio.
        </PageDescription>
      </div>
      <RegisterButton @click="openDialog">Criar Order Bump</RegisterButton>
    </div>

    <TableContainer>
      <div class="mb-6 flex items-center justify-between flex-wrap gap-4">
        <h2 class="text-xl font-semibold text-white">Order Bumps ativos</h2>
        
        <div class="flex flex-wrap items-center gap-4">
             <div class="relative">
            <Input
              v-model="searchQuery"
              placeholder="Buscar..."
              class="w-[280px]"
              @keydown.enter="fetchData()"
            >
              <template #prefix>
                <MagnifyingGlassIcon class="h-4 w-4 text-zinc-400" />
              </template>
            </Input>
          </div>
        </div>
      </div>

      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Título (Texto)</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Plano Gatilho</TableHead>
            <TableHead class="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="orderBumps">
            <TableFallback v-if="orderBumps.length === 0" :colspan="5" :icon="CreditCardIcon" title="Nenhum Order Bump encontrado" description="Clique em 'Criar Order Bump' para começar" />
            <TableRow v-for="bump in orderBumps" :key="bump.id">
              <TableCell class="font-medium text-white">{{ bump.name }}</TableCell>
              <TableCell class="text-zinc-300">{{ bump.title }}</TableCell>
              <TableCell class="text-zinc-300">
                {{ bump.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
              </TableCell>
              <TableCell class="text-zinc-300">
                <div class="flex flex-wrap gap-1">
                    <Badge v-for="plan in bump.paymentPlans" :key="plan.id">
                        {{ plan.name }}
                    </Badge>
                     <span v-if="!bump.paymentPlans?.length" class="text-zinc-500">-</span>
                </div>
              </TableCell>
              <TableCell class="text-right">
                <Button variant="ghost" size="icon" @click="editBump(bump)">
                  <PencilIcon class="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" class="hover:text-red-400" @click="confirmDelete(bump)">
                  <TrashIcon class="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton :columns="5" v-else />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="orderBumps">
        <template v-if="orderBumps.length > 0">
          <MobileCard v-for="bump in orderBumps" :key="bump.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Order Bump</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">{{ bump.name }}</span>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editBump(bump)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="confirmDelete(bump)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="Título">
                  {{ bump.title }}
               </MobileDataLabel>
               
               <MobileDataLabel label="Valor">
                   {{ bump.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
               </MobileDataLabel>

               <MobileDataLabel label="Planos Gatilho">
                  <div class="flex flex-wrap gap-1">
                      <Badge v-for="plan in bump.paymentPlans" :key="plan.id">
                          {{ plan.name }}
                      </Badge>
                       <span v-if="!bump.paymentPlans?.length" class="text-zinc-500">-</span>
                  </div>
               </MobileDataLabel>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <CreditCardIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum Order Bump encontrado
        </div>
      </div>
    </TableContainer>

    <!-- Dialog -->
    <Dialog v-model:open="isDialogOpen">
      <DialogScrollContent class="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{{ editingBump ? 'Editar Order Bump' : 'Novo Order Bump' }}</DialogTitle>
          <DialogDescription>
            Configure a oferta que aparecerá para o cliente.
            <br>Variáveis disponíveis no texto: <span class="text-emerald-400" v-pre>{{plano}}, {{valor}}, {{valor_total}}, {{nome}}</span>
          </DialogDescription>
        </DialogHeader>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            <!-- Left Column -->
            <div class="space-y-4">
                    <div class="space-y-1">
                        <Label>Nome Interno <span class="text-red-500">*</span></Label>
                        <Input v-model="form.name" placeholder="Ex: Oferta VIP" />
                        <span v-if="showErrors && errors.name" class="text-xs text-red-500">{{ errors.name }}</span>
                    </div>

                <div class="space-y-1">
                    <Label>Texto Explicativo (Título) <span class="text-red-500">*</span></Label>
                    <RichTextarea 
                        v-model="form.title" 
                        placeholder="Ex: Que tal levar também..." 
                        :rows="3" 
                        :variables="[
                            { label: 'Plano', value: '{{plano}}' },
                            { label: 'Valor', value: '{{valor}}' },
                            { label: 'Valor Total', value: '{{valor_total}}' },
                            { label: 'Nome', value: '{{nome}}' }
                        ]"
                    />
                     <span v-if="showErrors && errors.title" class="text-xs text-red-500">{{ errors.title }}</span>
                </div>
                
                 <div class="space-y-2">
                    <Label>Entregáveis (Descrição)</Label>
                    <RichTextarea 
                        v-model="form.deliverables" 
                        placeholder="Ex: Acesso ao grupo VIP..." 
                        :rows="3" 
                        :variables="[
                            { label: 'Nome', value: '{{nome}}' }
                        ]"
                    />
                </div>

                <div class="grid grid-cols-2 gap-4">
                     <div class="space-y-1">
                        <Label>Valor do Bump <span class="text-red-500">*</span></Label>
                        <Input v-model="form.amount" type="number" step="0.01" min="3" placeholder="0.00" />
                        <span v-if="showErrors && errors.amount" class="text-xs text-red-500">{{ errors.amount }}</span>
                    </div>
                     <div class="space-y-2">
                         <Label>Planos Gatilho <span class="text-red-500">*</span></Label>
                          <TagsInput v-if="filteredPaymentPlans" class="px-0 gap-0 w-full" :model-value="paymentPlanNames">
                              <div class="flex gap-2 flex-wrap items-center px-3">
                                <TagsInputItem v-for="(name, index) in paymentPlanNames" :key="index" :value="name">
                                  <TagsInputItemText />
                                  <TagsInputItemDelete @click="form.paymentPlanIds?.splice(index, 1); paymentPlanNames.splice(index, 1)" />
                                </TagsInputItem>
                              </div>
                              
                              <ComboboxRoot v-model="paymentPlanNames" v-model:open="isPaymentPlanComboboxOpen" v-model:search-term="searchPaymentPlan" class="w-full">
                                <ComboboxAnchor as-child>
                                  <ComboboxInput placeholder="Selecione os planos..." as-child>
                                    <TagsInputInput @focus="isPaymentPlanComboboxOpen = true" class="w-full px-3" :class="(form.paymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                                  </ComboboxInput>
                                </ComboboxAnchor>
                          
                                <ComboboxPortal>
                                  <ComboboxContent>
                                    <CommandList
                                      position="popper"
                                      class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                                    >
                                      <CommandEmpty>Nenhum plano encontrado</CommandEmpty>
                                      <CommandGroup>
                                        <CommandItem
                                          v-for="paymentPlan in filteredPaymentPlans" :key="paymentPlan.id" :value="String(paymentPlan.name)"
                                          @select.prevent="(ev) => {
                                            if (typeof ev.detail.value === 'string') {
                                              searchPaymentPlan = ''
                                              const paymentPlan = filteredPaymentPlans?.find(plan => plan.name == ev.detail.value)
                                              form.paymentPlanIds?.push(paymentPlan!.id)
                                              paymentPlanNames.push(paymentPlan!.name)
                                            }
                          
                                            if (filteredPaymentPlans?.length === 0) {
                                              isPaymentPlanComboboxOpen = false
                                            }
                                          }"
                                        >
                                          {{ paymentPlan.name }}
                                        </CommandItem>
                                      </CommandGroup>
                                    </CommandList>
                                  </ComboboxContent>
                                </ComboboxPortal>
                              </ComboboxRoot>
                            </TagsInput>
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                     <div class="space-y-1">
                        <Label>Texto Botão Aceitar <span class="text-red-500">*</span></Label>
                        <Input v-model="form.acceptButtonText" placeholder="Sim, eu quero!" />
                         <span v-if="showErrors && errors.acceptButtonText" class="text-xs text-red-500">{{ errors.acceptButtonText }}</span>
                    </div>
                    <div class="space-y-1">
                        <Label>Texto Botão Rejeitar <span class="text-red-500">*</span></Label>
                        <Input v-model="form.declineButtonText" placeholder="Não, obrigado" />
                         <span v-if="showErrors && errors.declineButtonText" class="text-xs text-red-500">{{ errors.declineButtonText }}</span>
                    </div>
                </div>
            </div>

            <!-- Right Column (Media) -->
            <div class="space-y-6">
                <div class="space-y-2">
                     <AnyFileUpload 
                        v-model="mediaFile" 
                        v-model:preview="mediaPreview" 
                        v-model:has-spoiler="form.mediaHasSpoiler"
                        label="Mídia (Imagem ou Vídeo)" 
                     />
                </div>
                <div class="space-y-2">
                    <AudioUpload 
                        v-model="audioFile" 
                        :initialPreviewUrl="audioPreview"
                        label="Áudio (Opcional)"
                    />
                </div>
            </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" @click="isDialogOpen = false">Cancelar</Button>
          <Button @click="save" :disabled="isSaving">
            <Loader2 v-if="isSaving" class="mr-2 h-4 w-4 animate-spin" />
            Salvar
          </Button>
        </DialogFooter>
      </DialogScrollContent>
    </Dialog>
    
    <DeleteConfirmationDialog 
        v-model:open="isDeleteDialogOpen" 
        :loading="isDeleting" 
        @confirm="confirmDeleteAction" 
    />
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import {
  MagnifyingGlassIcon,
  CreditCardIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { useOrderBump, CreateOrderBumpDTO, UpdateOrderBumpDTO } from '@/composables/useOrderBump'
import { usePaymentPlan } from '@/composables/usePaymentPlan'
import { useMedia } from '@/composables/useMedia'
import { OrderBump } from '@/interfaces/OrderBump'
import { PaymentPlan } from '@/interfaces/PaymentPlan'
import { useToast } from '@/components/ui/toast'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import { Loader2 } from 'lucide-vue-next'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import AnyFileUpload from '@/components/ui/upload/AnyFileUpload.vue'
import AudioUpload from '@/components/ui/upload/AudioUpload.vue'
import RichTextarea from '@/components/ui/textarea/RichTextarea.vue'
import { Badge } from '@/components/ui/badge'
import {
  TagsInput,
  TagsInputInput,
  TagsInputItem,
  TagsInputItemDelete,
  TagsInputItemText,
} from '@/components/ui/tags-input'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxPortal,
  ComboboxRoot,
} from 'radix-vue'
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

const { getAll, create, update, destroy } = useOrderBump()
const { getAll: getAllPlans } = usePaymentPlan()
const { upload } = useMedia()
const { toast } = useToast()

const orderBumps = ref<OrderBump[] | null>(null)
const paymentPlans = ref<PaymentPlan[]>([])
const searchQuery = ref('')
const isDialogOpen = ref(false)
const isSaving = ref(false)
const editingBump = ref<OrderBump | null>(null)

// Form
const form = ref({
    name: '',
    title: '',
    deliverables: '',
    amount: '',
    paymentPlanIds: [] as number[],
    acceptButtonText: '',
    declineButtonText: '',
    mediaHasSpoiler: false
})

const paymentPlanNames = ref<string[]>([])
const searchPaymentPlan = ref('')
const isPaymentPlanComboboxOpen = ref(false)
const showErrors = ref(false)

const filteredPaymentPlans = computed(() => {
    return paymentPlans.value.filter(plan => !form.value.paymentPlanIds.includes(plan.id) && plan.name.toLowerCase().includes(searchPaymentPlan.value.toLowerCase()))
})


const mediaFile = ref<File>()
const mediaPreview = ref('')
const audioFile = ref<File>()
const audioPreview = ref('')

// Delete
const isDeleteDialogOpen = ref(false)
const isDeleting = ref(false)
const bumpToDelete = ref<OrderBump | null>(null)

const errors = computed(() => {
    const errs: any = {}
    if (!form.value.name) errs.name = 'Nome é obrigatório'
    if (!form.value.title) errs.title = 'Título é obrigatório'
    if (!form.value.amount) {
        errs.amount = 'Valor é obrigatório'
    } else if (Number(form.value.amount) < 3) {
        errs.amount = 'O valor mínimo é R$ 3,00'
    }
    if (!form.value.acceptButtonText) errs.acceptButtonText = 'Texto do botão aceitar é obrigatório'
    if (!form.value.declineButtonText) errs.declineButtonText = 'Texto do botão rejeitar é obrigatório'
    return errs
})

const isValid = computed(() => {
    return Object.keys(errors.value).length === 0
})

const fetchData = async () => {
    orderBumps.value = null
    const [bumpsData, plansData] = await Promise.all([
        getAll(),
        getAllPlans({ page: '1', perPage: '100' }) // Fetch all plans
    ])
    orderBumps.value = bumpsData
    paymentPlans.value = plansData.data
}

onMounted(() => {
    fetchData()
})

const openDialog = () => {
    editingBump.value = null
    form.value = {
        name: '',
        title: '',
        deliverables: '',
        amount: '',
        paymentPlanIds: [],
        acceptButtonText: 'Sim, eu quero!',
        declineButtonText: 'Não, obrigado',
        mediaHasSpoiler: false
    }
    paymentPlanNames.value = []
    mediaFile.value = undefined
    mediaPreview.value = ''
    audioFile.value = undefined
    audioPreview.value = ''
    isDialogOpen.value = true
    showErrors.value = false
}

const editBump = (bump: OrderBump) => {
    editingBump.value = bump
    
    // Extract payment plans
    const connectedPlans = (bump as any).paymentPlans || [];
    const planIds = connectedPlans.map((p: any) => p.id);
    const planNames = connectedPlans.map((p: any) => p.name);

    form.value = {
        name: bump.name,
        title: bump.title,
        deliverables: bump.deliverables || '',
        amount: String(bump.amount),
        paymentPlanIds: planIds,
        acceptButtonText: bump.acceptButtonText,
        declineButtonText: bump.declineButtonText,
        mediaHasSpoiler: bump.mediaHasSpoiler
    }
    paymentPlanNames.value = planNames
    mediaFile.value = undefined
    mediaPreview.value = bump.mediaUrl || ''
    audioFile.value = undefined
    audioPreview.value = bump.audioUrl || ''
    isDialogOpen.value = true
    showErrors.value = false
}

const save = async () => {
    showErrors.value = true
    if (!isValid.value) return

    isSaving.value = true
    try {
        let mediaUrl = editingBump.value?.mediaUrl
        let mediaType = editingBump.value?.mediaType
        let audioUrl = editingBump.value?.audioUrl

        if (mediaFile.value) {
            const uploaded = await upload(mediaFile.value)
            mediaUrl = uploaded.source
            mediaType = uploaded.type.toLowerCase() // Ensure consistency
        }
        
        if (audioFile.value) {
            const uploaded = await upload(audioFile.value)
            audioUrl = uploaded.source
        }

        const payload: any = {
            name: form.value.name,
            title: form.value.title,
            deliverables: form.value.deliverables,
            amount: Number(form.value.amount),
            paymentPlanIds: form.value.paymentPlanIds,
            acceptButtonText: form.value.acceptButtonText,
            declineButtonText: form.value.declineButtonText,
            mediaUrl,
            mediaType,
            audioUrl,
            mediaHasSpoiler: form.value.mediaHasSpoiler
        }

        if (editingBump.value) {
            await update(editingBump.value.id, payload)
            toast({ title: 'Order Bump atualizado!', class: 'bg-green-500 text-white' })
        } else {
            await create(payload)
            toast({ title: 'Order Bump criado!', class: 'bg-green-500 text-white' })
        }
        
        isDialogOpen.value = false
        fetchData()
    } catch (error) {
        console.error(error)
        toast({ title: 'Erro ao salvar', variant: 'destructive' })
    } finally {
        isSaving.value = false
    }
}

const confirmDelete = (bump: OrderBump) => {
    bumpToDelete.value = bump
    isDeleteDialogOpen.value = true
}

const confirmDeleteAction = async () => {
    if (!bumpToDelete.value) return
    isDeleting.value = true
    try {
        await destroy(bumpToDelete.value.id)
        toast({ title: 'Order Bump excluído!', class: 'bg-green-500 text-white' })
        fetchData()
        isDeleteDialogOpen.value = false
    } catch (error) {
        toast({ title: 'Erro ao excluir', variant: 'destructive' })
    } finally {
        isDeleting.value = false
    }
}
</script>
